// AccountSettings.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth, API_BASE } from '../state/auth.jsx';
import { Camera, Plus, X, Upload, AlertCircle, Check, Loader2, Eye, EyeOff, User } from 'lucide-react';
const profileDefaultImage = '/profileImage.png';

const AccountSettings = () => {
    const { theme } = useOutletContext();
    const { api, me } = useAuth();
    const isLight = theme === 'light';

    const [profileImage, setProfileImage] = useState(null);
    const [email, setEmail] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showOptions, setShowOptions] = useState(false);
    const [isPlusHovered, setIsPlusHovered] = useState(false);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const fileInputRef = useRef(null);



    // Fetch user profile on mount
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await api.get('/user/profile');
                if (res.data?.success) {
                    if (res.data.user.profile_picture_url) {
                        setProfileImage(res.data.user.profile_picture_url);
                    } else {
                        // If no profile picture, load default helper but don't effectively "set" it as dirty unless they save
                        // Simply for display, renderAvatarContent handles fallback. 
                        // But if we want it to act like "default selected", we can do nothing and let renderAvatar handle fallback
                        // OR we can pre-set it. Let's let renderAvatar handle fallback visual.
                    }
                    setEmail(res.data.user.email);
                }
            } catch (err) {
                console.error("Failed to fetch profile", err);
            }
        };
        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Password validation
    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
    const passwordsDoNotMatch = newPassword && confirmPassword && newPassword !== confirmPassword;
    const passwordTooShort = newPassword && newPassword.length < 8;

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file); // Store file for upload
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileImage(reader.result);
                setShowOptions(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSetDefaultAvatar = async (e) => {
        if (e) e.stopPropagation();

        try {
            // Fetch default image
            const response = await fetch(profileDefaultImage);
            const blob = await response.blob();
            const file = new File([blob], "profile_default.png", { type: "image/png" });

            setSelectedFile(file);
            setProfileImage(profileDefaultImage); // For preview
        } catch (err) {
            console.error("Failed to load default profile image", err);
        }
        setShowOptions(false);
    };

    const handleUpdate = async () => {
        setLoading(true);
        setSuccessMessage('');
        try {
            let avatarUrl = profileImage;

            // 1. Upload file if selected
            if (selectedFile) {
                const formData = new FormData();
                formData.append('avatar', selectedFile);
                try {
                    const uploadRes = await api.post('/upload/user-avatar', formData);
                    if (uploadRes.data?.success) {
                        avatarUrl = uploadRes.data.imageUrl;
                    } else {
                        throw new Error(uploadRes.data?.error || "Upload failed");
                    }
                } catch (e) {
                    console.error("Avatar upload failed", e);
                    setSuccessMessage("Failed to upload image: " + (e.response?.data?.error || e.message));
                    setLoading(false);
                    return; // Stop execution if upload fails
                }
            }

            // SAFETY CHECK: Never send Base64 data to the profile endpoint
            if (typeof avatarUrl === 'string' && avatarUrl.startsWith('data:')) {
                setSuccessMessage("Error: Image upload failed. Cannot save Base64 data.");
                setLoading(false);
                return;
            }

            // 2. Prepare update data
            const updateData = {};
            // Only update if url changed (and is not Base64) or if it's a preset object
            if ((avatarUrl && avatarUrl !== profileImage) || (typeof profileImage === 'object')) {
                updateData.profile_picture_url = avatarUrl;
            }
            // Logic for presets
            if (typeof profileImage === 'object' && profileImage.type === 'preset') {
                updateData.profile_picture_url = JSON.stringify(profileImage);
            } else if (avatarUrl && typeof avatarUrl === 'string' && !avatarUrl.startsWith('data:')) {
                updateData.profile_picture_url = avatarUrl;
            }

            // 3. Call Update Profile Endpoint
            if (Object.keys(updateData).length > 0) {
                await api.put('/user/profile', updateData);
            }

            // 4. Handle Password Update
            if (passwordsMatch && newPassword) {
                try {
                    await api.put('/user/password', { newPassword });
                } catch (e) {
                    console.warn("Password update failed", e);
                }
            }

            setSuccessMessage("Profile updated successfully!");
            setTimeout(() => setSuccessMessage(''), 3000);

            // Clear password fields
            if (passwordsMatch) {
                setNewPassword('');
                setConfirmPassword('');
            }
            setSelectedFile(null);
            // Update local state to the new URL to avoid re-uploading
            if (avatarUrl && typeof avatarUrl === 'string') {
                setProfileImage(avatarUrl);
            }

            // Refresh global user context so Dashboard header updates immediately
            await me();

        } catch (error) {
            console.error("Update failed", error);
            setSuccessMessage("Update failed: " + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const renderAvatarContent = () => {
        if (!profileImage) {
            return (
                <img
                    src={profileDefaultImage}
                    alt="Default Profile"
                    className="h-full w-full object-cover rounded-full opacity-50"
                />
            );
        }

        let displayImage = profileImage;
        // Parse logic removed as presets are gone


        // Resolve absolute URL if it is a relative path from the API
        if (typeof displayImage === 'string' && displayImage.startsWith('/uploads')) {
            try {
                displayImage = `${new URL(API_BASE).origin}${displayImage}`;
            } catch (e) {
                console.error("Invalid API_BASE or image path", e);
            }
        }



        return (
            <img
                src={displayImage}
                alt="Profile"
                className="h-full w-full object-cover rounded-full"
                onError={(e) => console.error("Error loading image:", displayImage, e)}
            />
        );
    };

    useEffect(() => {
        const handleClickOutside = () => setShowOptions(false);
        if (showOptions) window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, [showOptions]);

    const inputBase = `w-full px-4 py-3 rounded-2xl border bg-transparent outline-none transition-all font-medium ${isLight
        ? 'border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900'
        : 'border-white/10 text-white placeholder:text-neutral-500 focus:border-white/30'
        }`;

    return (
        <div className="h-full overflow-y-auto pb-32 anim-slide-right px-4 sm:px-0">
            <div className={`max-w-4xl mx-auto rounded-3xl backdrop-blur-xl border p-8 sm:p-12 space-y-12 shadow-xl ${isLight
                ? 'bg-white/80 border-neutral-200'
                : 'bg-white/5 border-white/5'
                }`}>

                <div className="flex items-center justify-between mb-10">
                    <div>
                        <h3 className={`text-3xl font-bold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                            Account Settings
                        </h3>
                        <p className={`text-sm font-semibold mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                            Manage your profile and security credentials
                        </p>
                    </div>
                </div>

                <div className={`grid grid-cols-1 lg:grid-cols-3 gap-12 items-start pt-8 border-t ${isLight ? 'border-neutral-200' : 'border-white/5'}`}>
                    {/* Avatar Upload Section */}
                    <div className="flex flex-col items-center space-y-6">
                        <div className="relative">
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleImageUpload}
                            />

                            <div className={`h-48 w-48 rounded-full border-4 border-dashed flex items-center justify-center overflow-hidden transition-all shadow-2xl ${isLight
                                ? (isPlusHovered ? 'bg-neutral-50 border-neutral-900' : 'bg-neutral-50 border-neutral-200')
                                : (isPlusHovered ? 'bg-white/5 border-white' : 'bg-white/5 border-white/10')
                                }`}>
                                {renderAvatarContent()}
                            </div>

                            <div className="absolute bottom-2 right-2 z-20">
                                <button
                                    onMouseEnter={() => setIsPlusHovered(true)}
                                    onMouseLeave={() => setIsPlusHovered(false)}
                                    onClick={(e) => { e.stopPropagation(); setShowOptions(!showOptions); }}
                                    className={`h-12 w-12 rounded-full flex items-center justify-center shadow-2xl border-4 transition-all hover:scale-110 active:scale-95 ${isLight
                                        ? 'bg-neutral-900 text-white border-white'
                                        : 'bg-white text-black border-[#0A0C10]'
                                        }`}
                                >
                                    <Plus size={20} className={`transition-transform ${showOptions ? 'rotate-45' : ''}`} />
                                </button>

                                {showOptions && (
                                    <div
                                        onClick={(e) => e.stopPropagation()}
                                        className={`absolute bottom-14 right-0 w-56 rounded-2xl border p-2 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200 backdrop-blur-xl ${isLight ? 'bg-white border-neutral-200' : 'bg-[#14181F] border-white/10'
                                            }`}
                                    >
                                        <button
                                            onClick={handleSetDefaultAvatar}
                                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${isLight ? 'hover:bg-neutral-100 text-neutral-900' : 'hover:bg-white/5 text-white'
                                                }`}
                                        >
                                            <User size={18} className="opacity-60" />
                                            Default Avatar
                                        </button>
                                        <button
                                            onClick={() => fileInputRef.current.click()}
                                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${isLight ? 'hover:bg-neutral-100 text-neutral-900' : 'hover:bg-white/5 text-white'
                                                }`}
                                        >
                                            <Upload size={18} className="opacity-60" />
                                            Upload Custom
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="text-center">
                            <h4 className={`text-xs font-bold uppercase tracking-wider ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>
                                Profile Picture
                            </h4>
                            <p className={`text-xs mt-1 font-semibold ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                Visible on your dashboard
                            </p>
                        </div>
                    </div>

                    <div className="lg:col-span-2 space-y-10">
                        {/* Email Address */}
                        <div className="space-y-4">
                            <label className={`text-xs uppercase tracking-wider font-bold px-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                                Email Address
                            </label>
                            <input
                                type="text"
                                value={email}
                                readOnly
                                disabled
                                className={`w-full px-4 py-3 rounded-2xl border bg-transparent outline-none font-medium opacity-60 cursor-not-allowed ${isLight
                                    ? 'border-neutral-200 text-neutral-600 bg-neutral-50'
                                    : 'border-white/5 text-neutral-400 bg-white/5'
                                    }`}
                            />
                        </div>

                        {/* Password Section */}
                        <div className="space-y-6">
                            <h3 className={`text-lg font-bold flex items-center gap-3 ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                                Security
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className={`text-xs uppercase tracking-wider font-bold px-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'
                                        }`}>
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showNewPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={newPassword}
                                            autoComplete="new-password"
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className={inputBase}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className={`absolute right-4 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400 hover:text-neutral-600' : 'text-neutral-500 hover:text-neutral-300'}`}
                                        >
                                            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    {passwordTooShort && (
                                        <div className="flex items-center gap-2 text-xs text-orange-500 px-1">
                                            <AlertCircle size={12} />
                                            <span>At least 8 characters required</span>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <label className={`text-xs uppercase tracking-wider font-bold px-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'
                                        }`}>
                                        Confirm Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            autoComplete="new-password"
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className={inputBase}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className={`absolute right-4 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400 hover:text-neutral-600' : 'text-neutral-500 hover:text-neutral-300'}`}
                                        >
                                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    {passwordsDoNotMatch && (
                                        <div className="flex items-center gap-2 text-xs text-red-500 px-1">
                                            <X size={12} />
                                            <span>Passwords do not match</span>
                                        </div>
                                    )}
                                    {passwordsMatch && !passwordTooShort && (
                                        <div className="flex items-center gap-2 text-xs text-green-500 px-1">
                                            <Check size={12} />
                                            <span>Passwords match</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="pt-4">
                                {successMessage && (
                                    <div className="mb-4 p-3 rounded-xl bg-green-500/10 text-green-500 text-sm font-bold flex items-center gap-2">
                                        <Check size={16} />
                                        {successMessage}
                                    </div>
                                )}

                                <button
                                    onClick={handleUpdate}
                                    disabled={loading || (passwordTooShort || passwordsDoNotMatch)}
                                    className={`w-full py-4 rounded-2xl font-bold shadow-lg transition-all flex items-center justify-center gap-2 ${isLight
                                        ? 'bg-neutral-900 text-white shadow-neutral-900/20 hover:bg-neutral-800'
                                        : 'bg-white text-black shadow-white/5 hover:bg-neutral-200'
                                        } ${loading ? 'opacity-80' : ''} ${(passwordTooShort || passwordsDoNotMatch) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {loading && <Loader2 size={18} className="animate-spin" />}
                                    Update Account
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountSettings;

